import {
  AlertTriangleIcon,
  ArrowDownIcon,
  BookIcon,
  CheckIcon,
  CrossIcon,
} from 'assets/icons';
import { Button, ButtonStyle } from 'common/button';
import { Checkbox } from 'common/checkbox';
import { NotificationType } from 'common/enums';
import { InputSelect } from 'common/input-select';
import { Modal } from 'common/modal';
import { vaultsOrderByOptions, paginationDefault } from 'config/constants';
import { appActions, AppContext } from 'context';
import { classnames, getSubscriptionSchemeById } from 'helpers/utils';
import { debounce } from 'lodash';
import { VaultsQueryParams } from 'models/vaultsQueryParams';
import { ApiErrorArray } from 'models/api-errors-array';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Tooltip } from 'react-tooltip';
import styles from './modal-import-contributors.module.scss';

type ModalImportUsersProps = {
  userType: CollaboratorType;
  handleClose?: () => void;
  handleConfirm: () => void;
};

const ModalImportContributors: React.FC<ModalImportUsersProps> = ({
  userType,
  handleClose,
  handleConfirm,
}) => {
  const {
    dispatch,
    state: { currentVault, subscriptionsScheme },
  } = useContext(AppContext);
  const [vaults, setVaults] = useState<VaultType[]>([]);
  const [selectedVaultFrom, setSelectedVaultFrom] = useState<
    SelectType | undefined
  >(undefined);
  const [selectedVaultTo, setSelectedVaultTo] = useState<
    SelectType | undefined
  >(undefined);
  const [search, setSearch] = useState('');
  const [showContributorsList, setShowContributorsList] = useState(false);

  const vaultOptionsFrom = vaults
    .filter((a) => a.id !== currentVault?.id)
    .map((a) => ({
      id: a.id,
      value: a.title,
    }));

  const vaultOptions = vaults.map((a) => ({
    id: a.id,
    value: a.title,
  }));

  const contributorsList =
    vaults.find((a) => a.id === selectedVaultFrom?.id)?.contributors ?? [];

  const imageFrom = vaults.find(
    (a) => a.id === selectedVaultFrom?.id,
  )?.coverUrl;

  const [queryParams, setQueryParams] = useState<VaultsQueryParams>({
    pageSize: 6,
    pageNum: 1,
    sortBy: vaultsOrderByOptions.find(
      (elem: SelectType) => elem.id === paginationDefault.lastUpdate.id,
    )!.id,
  });

  const fetchVaults = async () => {
    try {
      const { data: vaultsData } =
        await VaultsController.getVaults(queryParams);

      setVaults(vaultsData);
    } catch (err: any) {
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Something went wrong',
          message: 'Please try again',
          type: NotificationType.Error,
        }),
      });
    }
  };

  useEffect(() => {
    fetchVaults();
  }, [queryParams]);

  const onSelectVaultFrom = (option: SelectType) => {
    if (selectedVaultFrom?.id !== option.id) {
      setSelectedVaultFrom(option);
    }
  };

  const handleDebounceFn = (value: string) => {
    const params = { ...queryParams };
    if (value === '') delete params.search;
    else params.search = value;

    setQueryParams({ ...params, pageNum: 1 });
  };

  const debounceFn = useCallback(debounce(handleDebounceFn, 500), [
    queryParams,
  ]);

  const onChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setSearch(value);
    debounceFn(value);
  };

  const [selectedIds, setSelectedIds] = useState<ContributorType[]>([]);

  const allSelected =
    contributorsList.length > 0 &&
    contributorsList.every((c) => selectedIds.includes(c));

  const subSelectedInfo = getSubscriptionSchemeById(
    subscriptionsScheme!,
    currentVault!.subscriptionType,
  );

  const maxContributorsLengthAllowed =
    subSelectedInfo!.permissions?.contributors;

  const contributorsCount = currentVault?.contributors?.length ?? 0;

  const contributorSlotsRemaining = maxContributorsLengthAllowed
    ? maxContributorsLengthAllowed - contributorsCount
    : 0;

  const contributorInvitesLeft = contributorSlotsRemaining - selectedIds.length;

  const isAtLimit = contributorInvitesLeft === 0;

  const existingContributors =
    currentVault !== undefined
      ? currentVault.contributors.map((a) => ({
          email: a.email,
        }))
      : [];

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      const limitedContributors = contributorsList
        .filter(
          (c): c is ContributorType =>
            c !== undefined &&
            !existingContributors.some(
              (existing) => existing.email === c.email,
            ),
        )
        .slice(0, contributorInvitesLeft);

      setSelectedIds(limitedContributors);
    }
  };

  const toggleOne = (contributor: ContributorType) => {
    setSelectedIds((prev) =>
      prev.some((c) => c.email === contributor.email)
        ? prev.filter((c) => c.email !== contributor.email)
        : [...prev, contributor],
    );
  };

  const showRightSelectVault = () => (
    <div className={styles.rightSide}>
      <div className={styles.containerRow}>
        {selectedVaultFrom && imageFrom ? (
          <img
            src={imageFrom}
            className={styles.picture}
            alt="Vault cover from"
          />
        ) : (
          <div className={styles.iconContainer}>
            <BookIcon />
          </div>
        )}
        <div className={styles.containerInput}>
          <p className="text__body__regular__tiny__gray500">FROM VAULT</p>
          <InputSelect
            id="vaultsSelectFrom"
            options={vaultOptionsFrom}
            optionSelected={selectedVaultFrom}
            onClickOption={onSelectVaultFrom}
            className={styles.clipSelect}
            innerClassName={styles.innerInput}
            placeholder="Type to search"
            searchValue={search}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeSearch(e)}
            emptyMessage="No vaults found"
          />
        </div>
      </div>
      <div className={styles.containerSeparator}>
        <div className={styles.iconBox}>
          <ArrowDownIcon />
        </div>
        <div className={styles.horizontalLine} />
      </div>
      <div className={styles.containerRow}>
        {currentVault && currentVault.coverUrl ? (
          <img
            src={currentVault.coverUrl}
            className={styles.picture}
            alt="Vault cover to"
          />
        ) : (
          <div className={styles.iconContainer}>
            <BookIcon />
          </div>
        )}
        <div className={styles.containerInput}>
          <p className="text__body__regular__tiny__gray500">TO VAULT</p>
          {currentVault ? (
            <p className="text__body__semi__bold__medium__gray700">
              {currentVault.title}
            </p>
          ) : (
            <InputSelect
              id="vaultsSelectTo"
              options={vaultOptions}
              optionSelected={selectedVaultTo}
              onClickOption={setSelectedVaultTo}
              className={styles.clipSelect}
              innerClassName={styles.innerInput}
              placeholder="Type to search"
              searchValue={search}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeSearch(e)}
              emptyMessage="No vaults found"
            />
          )}
        </div>
      </div>
    </div>
  );

  const showAllContributors = () => (
    <div className={styles.contentContributorsList}>
      <div className={styles.row}>
        <Checkbox
          itemKey="select-all"
          onChange={toggleAll}
          isChecked={allSelected}
          disabled={false}
        />
        <span className="text__body__regular__medium__gray700">
          <strong>All {contributorsList.length} contributors</strong> in vault{' '}
          <strong>{selectedVaultFrom?.value}</strong>
        </span>
      </div>
      <div className={styles.lineSeparator} />
      {contributorsList.map((contributor, index) => {
        const isSelected = selectedIds.includes(contributor);

        const alreadyInVault =
          contributor.email !== undefined
            ? existingContributors?.some((c) => c.email === contributor.email)
            : false;

        const shouldDisable = (!isSelected && isAtLimit) || alreadyInVault;
        const checkboxId = `contributor-${contributor.email}`;

        return (
          <>
            <div className={classnames(styles.row)}>
              <div
                data-tooltip-id={checkboxId}
                className={styles.containerTootip}
              >
                <div className={shouldDisable ? styles.disabledRow : ''}>
                  <Checkbox
                    itemKey={`contributor-${contributor.email}`}
                    onChange={() => {
                      toggleOne(contributor);
                    }}
                    isChecked={selectedIds.includes(contributor)}
                    disabled={shouldDisable}
                  />
                </div>
                <Tooltip
                  id={checkboxId}
                  place="bottom"
                  className={styles.tooltip}
                  hidden={!shouldDisable}
                >
                  <div
                    className={classnames(
                      styles.contentTooltip,
                      'text__body__regular__medium__gray700',
                    )}
                  >
                    {alreadyInVault ? (
                      <p>
                        This contributor is already in the destination vault
                      </p>
                    ) : (
                      <p>
                        Contributors limit reached. Deselect another contributor
                        to select this one
                      </p>
                    )}
                  </div>
                </Tooltip>
              </div>
              <div
                className={classnames(
                  styles.contentList,
                  shouldDisable ? styles.disabledRow : '',
                )}
              >
                <span
                  className={classnames(
                    styles.colum1,
                    'text__body__regular__medium__gray700',
                  )}
                >
                  {contributor.name}
                </span>
                <span
                  className={classnames(
                    styles.colum2,
                    'text__body__regular__medium__gray700',
                    alreadyInVault ? styles.maxwidth : '',
                  )}
                >
                  {contributor.email}
                </span>
              </div>
              {alreadyInVault && (
                <span
                  className={classnames(
                    styles.containerAlert,
                    'text__body__regular__tiny__gray700',
                  )}
                >
                  <AlertTriangleIcon className={styles.icon} />
                  ALREADY IN VAULT
                </span>
              )}
            </div>
            {index < contributorsList.length - 1 && (
              <div className={styles.lineSeparator} />
            )}
          </>
        );
      })}
    </div>
  );

  const showLeftSection = () => (
    <div className={styles.leftSide}>
      <p className="text__body__semi__bold__medium__gray700">
        Import contributors from an existing vault to this one
      </p>
      {showContributorsList && (
        <div className={styles.rightSide}>
          <div className={styles.containerRow}>
            {selectedVaultFrom && imageFrom ? (
              <img
                src={imageFrom}
                className={styles.picture}
                alt="Vault cover"
              />
            ) : (
              <div className={styles.iconContainer}>
                <BookIcon />
              </div>
            )}
            <div className={styles.containerInput}>
              <p className="text__body__regular__tiny__gray500">FROM VAULT</p>
              {selectedVaultFrom && (
                <p className="text__body__semi__bold__medium__gray700">
                  {selectedVaultFrom.value}
                </p>
              )}
            </div>
          </div>
          <div className={styles.containerSeparator}>
            <div className={styles.iconBox}>
              <ArrowDownIcon />
            </div>
            <div className={styles.horizontalLine} />
          </div>
          <div className={styles.containerRow}>
            {currentVault && currentVault.coverUrl ? (
              <img
                src={currentVault.coverUrl}
                className={styles.picture}
                alt="Vault cover left"
              />
            ) : (
              <div className={styles.iconContainer}>
                <BookIcon />
              </div>
            )}
            <div className={styles.containerInput}>
              <p className="text__body__regular__tiny__gray500">TO VAULT</p>
              {currentVault && (
                <p className="text__body__semi__bold__medium__gray700">
                  {currentVault.title}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      <p className="text__body__regular__medium__gray700">
        Contributor invites are limited by remaining invites in the destination
        vault.
      </p>
      {showContributorsList && (
        <div className={styles.invitesCounter} data-tooltip-id="Invites-left">
          <span className={classnames('text__body__regular__medium__gray700')}>
            <span className="text__body__semi__bold__medium__brand500">
              {contributorSlotsRemaining > 0 ? contributorInvitesLeft : 0}
            </span>{' '}
            invite{contributorSlotsRemaining !== 1 && 's'} left
          </span>
          <Tooltip id="Invites-left" place="bottom" className={styles.tooltip}>
            <div className="text__body__regular__medium__gray700">
              <div>You can add:</div>
              <div>
                <div>
                  <span>
                    1 contributor in <strong>Free Trial</strong>
                  </span>
                </div>
                <div>
                  <span>
                    5 contributors in <strong>Standard Vault</strong>
                  </span>
                </div>
                <div>
                  <span>
                    10 contributors in <strong>Lifetime/Legacy Vault</strong>
                  </span>
                </div>
              </div>
            </div>
          </Tooltip>
        </div>
      )}
    </div>
  );

  const handleContinue = () => {
    setShowContributorsList(true);
  };

  const showSuccessToast = () =>
    dispatch({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({
        show: true,
        title: `${selectedIds.length} contributors have been imported`,
        type: NotificationType.Success,
        icon: <CheckIcon />,
      }),
    });

  const handleImportContributors = async () => {
    try {
      if (!vaults) return;
      const body = selectedIds.map((user) => ({
        name: user.name,
        email: user.email,
      }));
      if (currentVault) {
        await VaultsController.addContributors(Number(currentVault.id), body);
      }
      showSuccessToast();
      handleConfirm();
    } catch (error) {
      const errorMessage =
        error instanceof ApiErrorArray
          ? error.errors[0].details
          : 'Please try again later.';
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: "Can't invite collaborator(s).",
          message: errorMessage,
          type: NotificationType.Error,
        }),
      });
    }
  };

  return (
    <Modal
      contentClassName={styles.container}
      onClose={handleClose}
      disableManualClosing
    >
      <button
        className={styles.closeButton}
        type="button"
        onClick={handleClose}
      >
        <CrossIcon />
      </button>
      <div className={styles.content}>
        <div className="text__heading4__brand500">Import {userType}s</div>
        <div className={styles.usersInfo}>
          {showLeftSection()}
          {!showContributorsList
            ? showRightSelectVault()
            : showAllContributors()}
        </div>
        <div className={classnames(styles.footer)}>
          <div className={styles.buttons}>
            <Button buttonStyle={ButtonStyle.GreyGhost} onClick={handleClose}>
              Close
            </Button>
            <Button
              buttonStyle={ButtonStyle.PrimaryFilled}
              disabled={
                showContributorsList
                  ? selectedIds.length === 0
                  : !selectedVaultFrom || !currentVault
              }
              onClick={
                showContributorsList ? handleImportContributors : handleContinue
              }
            >
              {showContributorsList
                ? `Import ${selectedIds.length} contributors`
                : 'Continue'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export { ModalImportContributors };
